/* "How this step works" copy for every screen, ported verbatim from the mock. Participant-facing: no em dashes. */
export interface Guide {
  time: string;
  need?: string[];
  steps: string[];
  done?: string;
  tip?: string;
}

export const GUIDES: Record<string, Guide> = {
  hub: {
    time: "Read once, 1 min",
    steps: [
      "Tap the card marked Do this to open your next step. Each step explains exactly what to do.",
      "Work through the GIG steps in order. Steps 1 to 4 make you Work-Enabled. Steps 5 and 6 happen once you're earning.",
      "Move the FINANCE steps along at the same time. They're short checklists that prepare you for the financing provider.",
      "Everything saves automatically. Sign in again with the same email and ID to pick up where you left off.",
    ],
    done: "You know which card to open next: it's the one marked Do this.",
  },
  audit: {
    time: "3 min",
    steps: [
      "Answer the five questions honestly. There are no wrong answers.",
      "Tap See my score. You get a starting score and tips on where to focus.",
      "Come back after Step 4 and see how far you've moved.",
    ],
    done: "You see your starting score.",
  },
  gig1: {
    time: "20 to 40 min",
    need: [
      "A phone or laptop with internet",
      "A government ID for the platform's identity check (KYC)",
      "A clear photo of yourself for your profile",
    ],
    steps: [
      "Write one line about who you help and what you do. The checks under the box turn green when it's clear.",
      "Pick a platform below and tap Open. Sign up through our link so your progress can be tracked.",
      "Complete your profile fully (photo, headline, skills) and start the platform's identity check (KYC).",
      "Copy your profile link from the browser's address bar, paste it into the box and tap Verify.",
      "When your niche line and at least one platform show green, tap Complete Step 1.",
    ],
    done: "Your niche line passes all 3 checks and at least one profile shows Verified.",
    tip: "Take a screenshot of your KYC status page on the platform. You'll upload it in Step 4.",
  },
  gig2: {
    time: "1 to 3 hours (the portfolio takes longest)",
    need: [
      "At least 3 pieces of your work (real or practice projects)",
      "Your CV, or 20 minutes to make one with a free builder",
    ],
    steps: [
      "Study the example portfolio. Each number matches one of the 10 things clients look for.",
      "Build or update your portfolio on one of the accepted sites. Paste the link and tap Verify.",
      "Open your portfolio and check off each of the 10 points it genuinely has. You need at least 7.",
      "Fill in the rate card: your income goal and hours give you an hourly guide, then set at least 2 packages.",
      "Upload your CV as a PDF or Word file.",
      "When all 5 parts show Done, tap Complete Step 2.",
    ],
    done: "All five parts show Done.",
    tip: "Your portfolio must open without a login or download. Test the link in a private browser window first.",
  },
  gig3: {
    time: "2 to 4 hours, over a few sessions",
    need: [
      "Your portfolio link from Step 2, to use as proof in your bids",
      "Your rate card, to price your bids",
    ],
    steps: [
      "Browse the jobs. Use search and the filters to find ones that fit your niche.",
      "Tap Shortlist on at least 3 jobs you'd genuinely go for.",
      "Tap a job to open it and write a bid. Your first 2 bids show a model proposal to learn from.",
      "Submit. You'll see a score and exactly what to fix. A bid that doesn't count can be edited and resubmitted.",
      "Keep going until 10 bids are counted.",
      "Under Agreement, tap Write it, fill in your client agreement and save it.",
      "Tap Complete Step 3.",
    ],
    done: "10 bids counted, 3 jobs shortlisted, and your agreement saved.",
    tip: "Every winning bid makes 6 moves: the client's name, their need, your proof, your plan, your price, and a question.",
  },
  bid: {
    time: "10 to 15 min per bid",
    steps: [
      "Read the brief. Note the client's name and exactly what they need.",
      "If a model proposal is shown, read it and the notes on why it works. Don't copy it.",
      "Enter your price, inside the client's budget.",
      "Write 60 to 180 words: greet the client by name, name their need, link to similar work, explain your plan in 2 or 3 steps, give your price and timeline, and end with a question.",
      "Tap Submit bid. Fix anything marked with a red cross and submit again.",
    ],
    done: 'You see a green "Counted" message.',
    tip: "Never invent clients or results. Practice projects are fine as proof.",
  },
  agreement: {
    time: "15 min",
    steps: [
      "Check the details we filled in from your niche and rate card.",
      "Leave Client name blank to keep this as a reusable template.",
      "List exactly what the client gets, and what's not included.",
      "Fix anything with a red cross, then tap Save my agreement.",
      "Before any real job, use Copy text or Print to send it to the client.",
    ],
    done: 'All checks are green and you see "Agreement saved".',
  },
  gig4: {
    time: "1 to 2 hours, plus a few days waiting on peer reviews",
    need: [
      "A payment account you've opened and verified (for example Payoneer or Grey)",
      "A screenshot of your KYC status page on your gig platform",
      "About 30 minutes to review your peers' portfolios",
    ],
    steps: [
      "Start part 4, the readiness assessment, first. It waits on your peers, so the sooner the better.",
      "Part 1: select every payment method you've set up, then confirm your account is verified.",
      "Part 2: choose your KYC status on your platform and upload a screenshot of the status page.",
      "Part 3: build your invoice and save it.",
      "Part 4: review your peers' portfolios honestly, then check back for your score. You need 70% to pass.",
      "When all four parts show Done or Passed, tap Confirm Work-Enabled to get your Launch Plan.",
    ],
    done: "Payment set up, KYC screenshot uploaded, invoice saved, and a passing assessment (70% or more).",
    tip: "Scored below 70%? On your result page, use Improve and resubmit. Peers then review the new version.",
  },
  invoice: {
    time: "10 min",
    steps: [
      "Keep or change the invoice number.",
      "Add the client's name. A practice client is fine.",
      "Add at least one line: what you delivered and the amount.",
      "Say how the client pays you. Don't type full account numbers.",
      "Tap Save my invoice. Use Print to save a PDF you can send.",
    ],
    done: 'All checks are green and you see "Invoice saved".',
  },
  review: {
    time: "5 to 10 min per portfolio",
    steps: [
      "Tap Start reviewing. Each portfolio link opens in a new tab.",
      "Answer 6 questions on a 1 to 5 scale, as if you were a paying client.",
      "Check off only what you can actually see on their page, then submit.",
      "Do the next one. Your own score unlocks when your reviews are done and enough peers have reviewed you.",
    ],
    done: "All the review dots are green.",
    tip: "Reviews are anonymous. Honest, fair scores are how everyone gets useful feedback.",
  },
  launch: {
    time: "5 min",
    steps: [
      "Read it top to bottom and check your details are right.",
      "Tap Print or save as PDF and keep a copy on your phone.",
      'Start the "next 7 days" list today.',
    ],
  },
  gig5: {
    time: "Alongside your first live gig",
    need: ["A real, paid gig that's in progress or just finished"],
    steps: [
      "Enter your live gig's details.",
      "Check off each client-management item as it genuinely happens. You can come back as the gig moves along.",
      'Write a short review request (25+ words), send it to your client, then check "I\'ve sent it".',
      'Write a repeat-work or retainer pitch (40+ words), send it, then check "I\'ve sent it".',
      "When all three parts show Done, tap Complete Step 5.",
    ],
    done: "All 8 checklist items checked and both messages sent.",
    tip: "No gig yet? Keep sending proposals using your Step 3 structure. This step will be here when you land one.",
  },
  gig6: {
    time: "30 to 45 min",
    need: [
      "Your gig earnings for each of the last 3 months (0 is fine for a month with none)",
      "Your monthly essential costs (rent, food, transport, data)",
    ],
    steps: [
      "Enter your last 3 months of gig earnings and your monthly essentials.",
      "Set your needs, wants and savings split so it adds up to 100%. Read any warning that appears.",
      "Pick a day for a weekly 15-minute money review.",
      "Fill in your savings goal, tracking tool, 90-day target, 3 actions and next skill.",
      "Tap Complete the pathway.",
    ],
    done: "Both parts show Done.",
    tip: "Always budget from your lowest month, and save the extra in good months.",
  },
  fin1: {
    time: "2 min",
    steps: [
      "Tap every service you'd like to be considered for. You can pick more than one.",
      "Tap Complete Finance Step 1. You can come back and change your choice later.",
    ],
    done: "At least one service is selected.",
  },
  fin2: {
    time: "5 min here, plus time to gather documents",
    steps: [
      "Read each item and the tip under it.",
      "Gather anything you don't have yet. Don't type numbers or upload anything here.",
      "Check off each item once you actually have it ready.",
      "When every item is checked, tap Complete.",
    ],
    done: "Every item is checked.",
    tip: "Your name must be spelled the same way on your ID, BVN and bank account.",
  },
  fin3: {
    time: "5 min here, plus time to gather records",
    steps: [
      "Read each item and the tip under it.",
      "Gather your records. Don't upload anything here.",
      "Check off each item once you have it ready. The guarantor item is optional.",
      "When every required item is checked, tap Complete.",
    ],
    done: "Every required item is checked.",
    tip: "Download your bank statement from your bank app early. Some banks take a few days.",
  },
  fin4: {
    time: "2 min",
    steps: [
      "Answer the 4 questions honestly. Only your readiness level is saved.",
      "Read the fix for anything that's flagged.",
      "Tap Save my result. Come back and update it after you fix things.",
    ],
    done: "All 4 questions are answered and your result is saved.",
    tip: "You need at least Nearly Ready to be referred in Finance Step 6.",
  },
  fin5: {
    time: "Completes with gig Step 5",
    steps: [
      "Finish gig Step 5 (Run It).",
      "Check the box once your LMS modules up to Step 5 are done.",
      "Tap Complete Finance Step 5.",
    ],
    done: "Gig Step 5 is done and the box is checked.",
  },
  fin6: {
    time: "Automatic",
    steps: [
      "This page checks three things for you: Finance Steps 1 to 5, gig Step 6, and your readiness level.",
      "Fix anything with a red cross by following its note.",
      "Once all three are green, you're referred and your provider will contact you.",
    ],
    done: 'The heading says "Referred to Your Provider".',
  },
};
